import React, { useMemo, useState } from 'react';
import { Archive, FileText, Search, Eye } from 'lucide-react';
import { ServiceTicket } from '../types';
import { calculateTicketTotal, formatDateTR, formatTL, STATUS_LABELS } from '../utils/storage';

interface HiddenArchiveViewProps {
  tickets: ServiceTicket[];
  onSelectTicket: (ticket: ServiceTicket) => void;
  onRestoreTicket: (ticket: ServiceTicket) => void;
}

export const HiddenArchiveView: React.FC<HiddenArchiveViewProps> = ({ tickets, onSelectTicket, onRestoreTicket }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTickets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const sorted = [...tickets].sort((a, b) =>
      String(b.deletedAt || b.updatedAt || b.createdAt || '').localeCompare(
        String(a.deletedAt || a.updatedAt || a.createdAt || '')
      )
    );

    if (!q) return sorted;
    return sorted.filter(ticket =>
      [
        ticket.id,
        ticket.customerName,
        ticket.customerPhone,
        ticket.deviceType,
        ticket.deviceBrand,
        ticket.deviceModel,
        ticket.serialNumber,
        ticket.deletedByName,
        ticket.deletedByUser,
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(q))
    );
  }, [tickets, searchQuery]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-4 sm:px-5 py-4 bg-amber-50 border-b border-amber-200">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-800">
                <Archive className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-black text-amber-950">Yönetici Gizli Form Arşivi</h2>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Gizlenen formlar yalnızca yöneticiler tarafından görüntülenir; cari ve rapor hesaplarına dahil edilmez.
                </p>
              </div>
              <span className="px-2 py-1 rounded-md bg-amber-100 border border-amber-200 text-amber-800 text-[10px] font-black">
                {tickets.length} KAYIT
              </span>
            </div>
          </div>

          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Form no, müşteri, telefon, cihaz ara..."
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-amber-200 bg-white text-xs font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        {filteredTickets.length === 0 ? (
          <div className="py-16 px-4 text-center">
            <Archive className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="text-sm font-bold text-slate-700">
              {tickets.length === 0 ? 'Gizli form arşivi boş.' : 'Aramanızla eşleşen gizli form bulunamadı.'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-bold">Form No</th>
                  <th className="px-4 py-3 font-bold">Müşteri</th>
                  <th className="px-4 py-3 font-bold">Cihaz</th>
                  <th className="px-4 py-3 font-bold">Servis Durumu</th>
                  <th className="px-4 py-3 font-bold">Tutar</th>
                  <th className="px-4 py-3 font-bold">Gizlenme</th>
                  <th className="px-4 py-3 font-bold">Gizleyen</th>
                  <th className="px-4 py-3 font-bold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTickets.map(ticket => {
                  const { grandTotal } = calculateTicketTotal(ticket.parts, ticket.laborCost, ticket.vatRate, ticket.discount, ticket.isVatIncluded, ticket.roundingAdjustment || 0);
                  return (
                    <tr key={ticket.id} className="hover:bg-amber-50/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono text-xs font-black text-blue-700">#{ticket.id}</span>
                        <div className="mt-0.5 text-[9px] font-bold text-amber-700">GİZLİ</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-bold text-slate-900">{ticket.customerName || '-'}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">{ticket.customerPhone || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-semibold text-slate-800">{ticket.deviceBrand || '-'} {ticket.deviceModel || ''}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{ticket.deviceType || '-'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 rounded-md bg-slate-100 text-[10px] font-bold text-slate-700">
                          {STATUS_LABELS[ticket.status] || ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs font-black font-mono text-slate-800">{formatTL(grandTotal)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-[11px] font-semibold text-slate-700">{ticket.deletedAt ? formatDateTR(ticket.deletedAt) : '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-[11px] font-semibold text-slate-600 whitespace-nowrap">{ticket.deletedByName || ticket.deletedByUser || 'Yönetici'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onSelectTicket(ticket)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-[10px] font-bold text-amber-900 transition-colors cursor-pointer"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Formu Aç
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`#${ticket.id} numaralı form tekrar görünür hale getirilsin mi?`)) {
                                onRestoreTicket(ticket);
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-[10px] font-bold text-emerald-800 transition-colors cursor-pointer"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Görünür Yap
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
